import { useState, useEffect, useMemo } from "react";
import { fetchMsdsList, fetchMsdsHistory, saveMsds, downloadPdf } from "../../apiService";
import { useToast } from "../../components/Toast";

const TYPE_LABELS = {
  WATER_BASED: "Water Based",
  HOTMELT:     "Hotmelt",
};

function openMsdsPdf(id, code, version) {
  downloadPdf(
    `/api/qc/msds/${id}/pdf`,
    `${(code ?? "MSDS").replace(/[^A-Za-z0-9_-]/g, "_")}_MSDS_v${version}.pdf`
  );
}

// ── Create / Edit modal ──────────────────────────────────────────────────────
function MsdsModal({ fgs, initial, onSave, onClose }) {
  const showToast = useToast();
  const [form, setForm] = useState(initial ?? {
    fgId: "", productName: "", productCode: "", msdsType: "WATER_BASED",
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState(null);

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function handleFgChange(fgId) {
    const fg = fgs.find(f => f.id === fgId);
    const isHm = fgId.toLowerCase().startsWith("hm");
    setForm(f => ({
      ...f,
      fgId,
      productCode:  fg ? fg.id : fgId,
      productName:  fg?.name ?? "",
      msdsType:     isHm ? "HOTMELT" : "WATER_BASED",
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      const saved = await saveMsds(form);
      showToast("MSDS saved");
      onSave(saved);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal" style={{ width: 540 }}>
      <div className="modal-header">
        <h3 className="modal-title">{initial ? "Update MSDS (new version)" : "New Material Safety Data Sheet"}</h3>
        <button className="btn" type="button" onClick={onClose}>✕</button>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label className="form-label">Finished Good *</label>
            <select className="form-input" style={{ width: "100%" }} value={form.fgId}
              onChange={e => handleFgChange(e.target.value)} required>
              <option value="">— select —</option>
              {fgs.map(f => <option key={f.id} value={f.id}>{f.name || f.id}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">MSDS Type *</label>
            <select className="form-input" style={{ width: "100%" }} value={form.msdsType}
              onChange={e => setF("msdsType", e.target.value)} required>
              <option value="WATER_BASED">Water Based — AMI Logo</option>
              <option value="HOTMELT">Hotmelt — General Trading Logo</option>
            </select>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <label className="form-label">Product Name *</label>
            <input className="form-input" style={{ width: "100%" }} value={form.productName}
              onChange={e => setF("productName", e.target.value)}
              required placeholder="e.g. STRONG BOND WATER BASED ADHESIVES" />
          </div>
          <div>
            <label className="form-label">Product Code *</label>
            <input className="form-input" style={{ width: "100%" }} value={form.productCode}
              onChange={e => setF("productCode", e.target.value)}
              required placeholder="e.g. SBPK 500" />
          </div>
        </div>

        <div style={{
          background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6,
          padding: "8px 12px", fontSize: 12, color: "#0369a1", marginBottom: 12,
        }}>
          The MSDS content is standardised for each type. Only the product name and code appear on the document.
        </div>

        {err && <div className="alert-warn" style={{ marginBottom: 12 }}>{err}</div>}

        <div className="form-footer">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Save as new version"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Version history panel ────────────────────────────────────────────────────
function HistoryPanel({ fgId, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMsdsHistory(fgId)
      .then(setHistory)
      .finally(() => setLoading(false));
  }, [fgId]);

  return (
    <div className="modal" style={{ width: 540 }}>
      <div className="modal-header">
        <h3 className="modal-title">MSDS Version History</h3>
        <button className="btn" type="button" onClick={onClose}>✕</button>
      </div>
      {loading && <p style={{ color: "var(--color-text-secondary)" }}>Loading…</p>}
      {!loading && history.map(m => (
        <div key={m.id} style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 8,
          marginBottom: 8, background: m.isCurrent ? "var(--color-violet-tint)" : "var(--color-bg)",
        }}>
          <div>
            <span style={{ fontWeight: 700, color: "var(--color-violet)", marginRight: 8 }}>v{m.version}</span>
            {m.isCurrent && <span className="badge badge-blue" style={{ marginRight: 8 }}>Current</span>}
            <span style={{ fontSize: 13 }}>{m.productCode}</span>
            <span style={{ fontSize: 11, color: "var(--color-text-secondary)", marginLeft: 8 }}>
              {TYPE_LABELS[m.msdsType] ?? m.msdsType}
            </span>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>
              Saved {new Date(m.createdAt).toLocaleDateString("en-GB",
                { day: "numeric", month: "short", year: "numeric" })}
              {" "}&mdash; by {m.createdBy}
            </div>
          </div>
          <button className="btn" style={{ fontSize: 12 }} type="button"
            onClick={() => openMsdsPdf(m.id, m.productCode, m.version)}>
            ↓ PDF
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export function PageMsds({ myPerms, fgs }) {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");
  const [modal,   setModal]   = useState(null);

  const canEdit = myPerms?.includes("QC_EDIT");

  useEffect(() => {
    fetchMsdsList()
      .then(setList)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return list.filter(m =>
      m.productName.toLowerCase().includes(q) ||
      m.productCode.toLowerCase().includes(q) ||
      (TYPE_LABELS[m.msdsType] ?? m.msdsType).toLowerCase().includes(q)
    );
  }, [list, search]);

  function onSaved(msds) {
    setList(prev => {
      const without = prev.filter(m => m.fgId !== msds.fgId);
      return [...without, msds].sort((a, b) => a.productCode.localeCompare(b.productCode));
    });
    setModal(null);
  }

  return (
    <div style={{ padding: "0 0 40px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0 }}>Material Safety Data Sheets</h2>
          <p style={{ margin: "4px 0 0", color: "var(--color-text-secondary)", fontSize: 14 }}>
            Version-controlled MSDS library — Water Based and Hotmelt grades
          </p>
        </div>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setModal({ type: "create" })}>
            + New MSDS
          </button>
        )}
      </div>

      <input className="form-input" style={{ width: 320, marginBottom: 18 }}
        placeholder="Search by product name, code or type…"
        value={search} onChange={e => setSearch(e.target.value)} />

      {loading && <p style={{ color: "var(--color-text-secondary)" }}>Loading…</p>}
      {error   && <div className="alert-warn">{error}</div>}

      {!loading && !error && filtered.length === 0 && (
        <div className="alert-info">
          No MSDS records yet.{canEdit && ' Click "+ New MSDS" to create the first one.'}
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product Code</th>
                <th>Product Name</th>
                <th>Type</th>
                <th>Version</th>
                <th>Issue Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.id}>
                  <td style={{ fontWeight: 600 }}>{m.productCode}</td>
                  <td>{m.productName}</td>
                  <td>
                    <span style={{
                      fontSize: 11, padding: "2px 7px", borderRadius: 3, fontWeight: 600,
                      background: m.msdsType === "HOTMELT" ? "#fdf4ff" : "#eff6ff",
                      color:      m.msdsType === "HOTMELT" ? "#7e22ce" : "#1d4ed8",
                      border:     `1px solid ${m.msdsType === "HOTMELT" ? "#e9d5ff" : "#bfdbfe"}`,
                    }}>
                      {TYPE_LABELS[m.msdsType] ?? m.msdsType}
                    </span>
                  </td>
                  <td><span className="badge badge-blue">v{m.version}</span></td>
                  <td style={{ color: "var(--color-text-secondary)" }}>
                    {new Date(m.createdAt).toLocaleDateString("en-GB",
                      { day: "numeric", month: "short", year: "numeric" })}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn" style={{ fontSize: 12 }} type="button"
                        onClick={() => openMsdsPdf(m.id, m.productCode, m.version)}>
                        PDF
                      </button>
                      {canEdit && (
                        <button className="btn" style={{ fontSize: 12 }} type="button"
                          onClick={() => setModal({ type: "edit", data: {
                            fgId: m.fgId, productName: m.productName,
                            productCode: m.productCode, msdsType: m.msdsType,
                          }})}>
                          Edit
                        </button>
                      )}
                      <button className="btn" style={{ fontSize: 12 }} type="button"
                        onClick={() => setModal({ type: "history", data: m.fgId })}>
                        History
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          {(modal.type === "create" || modal.type === "edit") && (
            <MsdsModal
              fgs={fgs ?? []}
              initial={modal.type === "edit" ? modal.data : null}
              onSave={onSaved}
              onClose={() => setModal(null)} />
          )}
          {modal.type === "history" && (
            <HistoryPanel fgId={modal.data} onClose={() => setModal(null)} />
          )}
        </div>
      )}
    </div>
  );
}
